import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  Grid,
  CircularProgress,
  Snackbar,
  Container,
  Slide,
  Stack
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { containerService } from '../../services/api';
import { CreateContainerData } from '../../types/locker';
import { Autocomplete } from "@react-google-maps/api";

import { useRef } from 'react';


export const AddContainer: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [ loading, setLoading ] = useState( false );
  const [ toast, setToast ] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>( {
    open: false,
    message: '',
    severity: 'success'
  } );
  const [ formData, setFormData ] = useState<CreateContainerData>( {
    containerNumber: '',
    containerName: '',
    serialNumber: '',
    address: '',
    zipCode: '',
    latitude: 0,
    longitude: 0,
  } );
  const [ imeiNumber, setImeiNumber ] = useState( '' );
  const [ autoCompleteEnabled, setAutoCompleteEnabled ] = useState( true )
  const autoCompleteRef = useRef<any>( null );
  const addressInputRef = useRef<HTMLInputElement | null>( null );



  useEffect( () => {
    // Trigger automatically when API key is wrong → InvalidKeyMapError
    ( window as any ).gm_authFailure = () => {
      console.error( "❌ Invalid Google API Key → Switching to manual address input" );
      setAutoCompleteEnabled( false );

      // Reset only address, not other fields
      setTimeout( () => {
        if ( addressInputRef.current ) {
          const len = addressInputRef.current.value.length;

          addressInputRef.current.focus();
          addressInputRef.current.setSelectionRange( len, len ); // cursor stays at end
        }
      }, 50 );;
    };
  }, [] );







  const handleChange = ( field: keyof CreateContainerData ) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData( {
      ...formData,
      [ field ]: event.target.value
    } );
  };

  const handleSubmit = async ( event: React.FormEvent ) => {
    event.preventDefault();
    try {
      setLoading( true );
      const response = await containerService.createContainer( {
        containerNumber: formData.containerNumber,
        containerName: formData.containerName,
        imeiNumber: imeiNumber,
        serialNumber: formData.serialNumber,
        address: formData.address,
        latitude: formData.latitude.toString(),
        longitude: formData.longitude.toString(),
        zipCode: formData.zipCode
      } );

      if ( response.success ) {
        setToast( {
          open: true,
          message: t( 'containers.create.success' ),
          severity: 'success'
        } );
        setTimeout( () => {
          navigate( '/lockers' );
        }, 1500 );
      } else {
        setToast( {
          open: true,
          message: response.message || 'Failed to create container',
          severity: 'error'
        } );
      }
    } catch ( error ) {
      console.error( 'Error creating container:', error );
      setToast( {
        open: true,
        message: 'Error creating container',
        severity: 'error'
      } );
    } finally {
      setLoading( false );
    }
  };

  const handleCloseToast = () => {
    setToast( { ...toast, open: false } );
  };

  if ( loading ) {
    return (
      <Container maxWidth="lg" sx={ { py: 4 } }>
        <Box sx={ {
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '400px',
          gap: 2
        } }>
          <CircularProgress size={ 60 } />
          <Typography variant="h6" color="text.secondary">
            Creating container...
          </Typography>
        </Box>
      </Container>
    );
  }



  const handlePlaceSelect = () => {
    const place = autoCompleteRef.current.getPlace();
    if ( !place ) return;

    const location = place.geometry?.location;

    setFormData( {
      ...formData,
      address: place.formatted_address || '',
      latitude: location?.lat() || 0,
      longitude: location?.lng() || 0,
    } );


  };


  return (
    <Container maxWidth="lg" sx={ { py: 4 } }>
      <Slide direction="down" in={ true } timeout={ 600 }>
        <Box>
          {/* Header Section */ }
          <Box sx={ { mb: 4 } }>
            <Stack direction="row" alignItems="center" spacing={ 2 } sx={ { mb: 2 } }>
              <Button
                variant="outlined"
                startIcon={ <ArrowBackIcon /> }
                onClick={ () => navigate( '/lockers' ) }
                sx={ { mr: 2 } }
              >
                Back
              </Button>
              <Box>
                <Typography variant="h4" fontWeight="bold" gutterBottom>
                  { t( 'containers.management.addContainer' ) }
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Create a new storage container with all necessary details
                </Typography>
              </Box>
            </Stack>
          </Box>

          {/* Form Content */ }
          <Paper
            elevation={ 0 }
            sx={ {
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              p: 4
            } }
          >
            <form onSubmit={ handleSubmit }>
              <Grid container spacing={ 3 }>
                <Grid item xs={ 12 } md={ 6 }>
                  <TextField
                    fullWidth
                    label={ t( 'containers.management.containerNumber' ) }
                    value={ formData.containerNumber }
                    onChange={ handleChange( 'containerNumber' ) }
                    required
                    placeholder="Enter container number"
                    inputProps={ { maxLength: 20 } }
                  />

                </Grid>
                <Grid item xs={ 12 } md={ 6 }>
                  <TextField
                    fullWidth
                    label={ t( 'containers.management.containerName' ) }
                    value={ formData.containerName }
                    onChange={ handleChange( 'containerName' ) }
                    required
                    placeholder="Enter container name"
                    inputProps={ { maxLength: 50 } }
                  />

                </Grid>
                <Grid item xs={ 12 } md={ 6 }>
                  <TextField
                    fullWidth
                    label={ t( 'containers.management.serialNumber' ) }
                    value={ formData.serialNumber }
                    onChange={ handleChange( 'serialNumber' ) }
                    required
                    placeholder="Enter serial number"
                    inputProps={ { maxLength: 30 } }
                  />

                </Grid>
                <Grid item xs={ 12 } md={ 6 }>
                  <TextField
                    fullWidth
                    label={ t( 'containers.management.imeiNumber' ) }
                    value={ imeiNumber }
                    onChange={ ( e ) => setImeiNumber( e.target.value ) }
                    required
                    placeholder="Enter IMEI number"
                    inputProps={ { maxLength: 15 } }
                  />

                </Grid>

                {/* 🔥 Google Autocomplete Address Field */ }
                { autoCompleteEnabled ? ( <Grid item xs={ 12 } md={ 6 }>



                  <Autocomplete
                    onLoad={ ( ref ) => ( autoCompleteRef.current = ref ) }
                    onPlaceChanged={ handlePlaceSelect } // use existing function
                  >
                    <TextField
                      fullWidth
                      label={ t( "containers.management.address" ) }
                      value={ formData.address }
                      placeholder="Search & select location or type your address"
                      required
                      sx={ { background: "#fff" } }
                      inputProps={ { maxLength: 200 } }
                      onChange={ ( e ) =>
                        setFormData( {
                          ...formData,
                          address: e.target.value,
                          latitude: 0, // reset lat/lng if user types manually
                          longitude: 0,
                        } )
                      }
                    />
                  </Autocomplete>
                </Grid> ) : ( <Grid item xs={ 12 } md={ 6 }>  <TextField

                  fullWidth
                  label="Address"
                  inputRef={ addressInputRef }
                  value={ formData.address }
                  inputProps={ { maxLength: 200 } }
                  placeholder="Enter address manually (Google disabled)"
                  onChange={ ( e ) =>
                    setFormData( {
                      ...formData,
                      address: e.target.value,
                      latitude: 0,
                      longitude: 0,
                    } )
                  }
                />
                </Grid>
                ) }






                {/* <TextField
                  fullWidth
                  label={ t( 'containers.management.address' ) }
                  value={ formData.address }
                  onChange={ handleChange( 'address' ) }
                  required
                  placeholder="Enter full address"
                  multiline
                  rows={ 3 }
                /> */}

                <Grid item xs={ 12 } md={ 6 }>
                  <TextField
                    fullWidth
                    label={ t( 'containers.management.zipCode' ) }
                    value={ formData.zipCode }
                    onChange={ handleChange( 'zipCode' ) }
                    required
                    placeholder="Enter zip code"
                    inputProps={ { maxLength: 10 } }
                  />

                </Grid>
              </Grid>

              <Box sx={ { mt: 4, display: 'flex', justifyContent: 'flex-end' } }>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  startIcon={ <SaveIcon /> }
                  disabled={ loading }
                  sx={ {
                    px: 3,
                    py: 1.5,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
                    '&:hover': {
                      boxShadow: '0 6px 16px rgba(25, 118, 210, 0.4)'
                    }
                  } }
                >
                  { loading ? 'Creating...' : t( 'common.save' ) }
                </Button>
              </Box>
            </form>
          </Paper>
        </Box>
      </Slide>

      <Snackbar
        open={ toast.open }
        autoHideDuration={ 6000 }
        onClose={ handleCloseToast }
        anchorOrigin={ { vertical: 'top', horizontal: 'right' } }
      >
        <Alert onClose={ handleCloseToast } severity={ toast.severity } sx={ { width: '100%' } }>
          { toast.message }
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AddContainer; 